import { jwtDecode } from 'jwt-decode';
import CryptoJS from 'crypto-js';
import { SessionState } from '../../store/slices/sessionSlice';
import { decryptToken } from './tokenCrypto';

// Shared key with the login Lambda (RESPONSE_ENC_KEY). The token is
// AES-256-CBC, base64( iv[16] || ciphertext ), key = SHA-256(cryptoSecretKey).
const TOKEN_ENC_KEY = (process.env.REACT_APP_API_cryptoSecretKey ?? '')
  .replace(/^['"]/, '')
  .replace(/['"],?$/, '');

/** AES-256-CBC decrypt the Lambda-issued token -> raw JWT string ('' on failure). */
function aesDecryptToken(encoded: string): string {
  try {
    if (!TOKEN_ENC_KEY) return '';
    const key = CryptoJS.SHA256(TOKEN_ENC_KEY); // 32-byte key
    const raw = CryptoJS.enc.Base64.parse(encoded);
    const words = raw.words;
    const iv = CryptoJS.lib.WordArray.create(words.slice(0, 4), 16);
    const ct = CryptoJS.lib.WordArray.create(words.slice(4), raw.sigBytes - 16);
    const dec = CryptoJS.AES.decrypt(
      { ciphertext: ct } as CryptoJS.lib.CipherParams,
      key,
      {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }
    );
    return dec.toString(CryptoJS.enc.Utf8);
  } catch {
    return '';
  }
}

const TOKEN_KEY = 'token';
const OFFICE_KEY_ID = 'officeKeyID';

// Mirrors Angular's UserSessionService.localStorageSessionKey
// ('DOF-' + environment.apiBaseUrl + '-AuthData') and the key written by
// `useAuthKey()`.
export const SESSION_STORAGE_KEY = `DOF-${process.env.REACT_APP_API_apiBaseUrl}-AuthData`;

// ── character-substitution tables (ported from Angular's CommonService) ──────

const SYMBOL_TO_LETTER: Record<string, string> = {
  '!': 'a',
  '@': 'B',
  '#': 'c',
  $: 'D',
  '%': 'e',
  '^': 'F',
  '&': 'g',
  '?': 'i',
  '+': 'J',
  '=': 'k',
  '|': 'L',
  '~': 'm',
  '/': 'N',
  '-': 'o',
  _: 'P',
  '{': 'q',
  '}': 'R',
  '[': 's',
  ']': 'T',
  '(': 'U',
  ')': 'v',
  '<': 'W',
  '>': 'x',
  ':': 'Y',
  ';': 'z',
  '*': '.',
};

const ALPHA_TO_NUMBER: Record<string, string> = {
  A: '0',
  b: '1',
  C: '2',
  d: '3',
  E: '4',
  f: '5',
  G: '6',
  h: '7',
  I: '8',
  j: '9',
};

// Equivalent of Angular's CommonService.assignLetters()
export function assignLetters(text: string): string {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += SYMBOL_TO_LETTER[text[i]] ?? text[i];
  }
  return result;
}

// Equivalent of Angular's CommonService.alphabetToNumber()
export function alphabetToNumber(text: string): string {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += ALPHA_TO_NUMBER[text[i]] ?? text[i];
  }
  return result;
}

// Equivalent of Angular's CommonService.decryptionData()
// Reverses the backend's symbol-substitution to produce a valid JWT string
export function decryptionData(token: string): string {
  return assignLetters(token);
}

// ── session persistence ──────────────────────────────────────────────────────

/**
 * `btoa` only accepts Latin-1, and token claims can carry Tamil text (school and
 * teacher names), so fall back to base64 of the UTF-8 bytes. Either form is
 * readable with `atob`, which is what Angular's `UserSessionService.load()` uses.
 */
function toBase64(json: string): string {
  try {
    return window.btoa(json);
  } catch {
    const bytes = new TextEncoder().encode(json);
    let binary = '';
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return window.btoa(binary);
  }
}

/**
 * Mirrors Angular's `UserSessionService.create()`, which `elogin()` called after
 * a successful login: the session is stored under `DOF-<apiBaseUrl>-AuthData` as
 * base64-encoded JSON.
 *
 * React reads its own session out of Redux, so this write exists for anything on
 * the origin that still follows the Angular contract - including the Angular app
 * itself while both run side by side during the migration.
 */
export function storeSession(session: Omit<SessionState, 'loaded'>): void {
  // Angular's `UserSession` model spells this one `teacherName`.
  const payload = { ...session, teacherName: session.teacher_name };
  localStorage.setItem(SESSION_STORAGE_KEY, toBase64(JSON.stringify(payload)));
}

// ── session teardown ─────────────────────────────────────────────────────────

/**
 * Clears the stored session. Angular's `LoginComponent.onLogout()` called
 * `localStorage.clear()`, but here that would also wipe the rotating
 * `AuthorizationKey` that `useAuthKey()` manages and that every API call needs,
 * so only the session-owned keys are removed.
 */
export function clearStoredSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(OFFICE_KEY_ID);
  localStorage.removeItem(SESSION_STORAGE_KEY);
}

// ── main decode function ─────────────────────────────────────────────────────

/**
 * The `/login` gateway returns the token AES-256-CBC encrypted
 * (FRONTEND_ENCRYPTION.md), so the AES-decrypted form is tried first. Older
 * `/api/elogin` responses handed back a plain JWT that Angular decoded directly,
 * so the raw value is tried next. Tokens that arrive through the symbol-encoded
 * gateway need the substitution reversed before they parse, kept as the last
 * fallback. Each candidate that fails to decrypt/decode is skipped.
 */
function decodeToken(
  raw: string
): { jwt: string; claims: Record<string, unknown> } | null {
  const candidates = [decryptToken(raw), raw, decryptionData(raw)];
  for (const candidate of candidates) {
    if (!candidate) continue;
    try {
      return {
        jwt: candidate,
        claims: jwtDecode<Record<string, unknown>>(candidate),
      };
    } catch {
      // fall through and try the next representation
    }
  }
  return null;
}

export function decodeSessionFromStorage(): Omit<
  SessionState,
  'loaded'
> | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;

    const decoded = decodeToken(raw);
    if (!decoded) return null;

    const { jwt, claims: d } = decoded;

    const usertypeid = String(d['emis_usertype'] ?? '');
    const isOfficeUser = [5, 6, 9, 10, 22].includes(Number(usertypeid));
    if (isOfficeUser && d['off_key_id']) {
      localStorage.setItem(OFFICE_KEY_ID, String(d['off_key_id']));
    }

    return {
      // identity
      userId: Number(d['emis_user_id'] ?? d['user_id']) || 0,
      username: String(d['emis_username'] ?? ''),
      emis_username: String(d['emis_username'] ?? ''),
      name: String(d['name'] ?? ''),
      teacher_name: String(d['teacher_name'] ?? ''),
      Socialname: String(d['Socialname'] ?? ''),
      designation: String(d['designation'] ?? ''),
      phnumber: String(d['mbl_nmbr'] ?? ''),
      email: String(d['email'] ?? ''),

      // school
      schoolName: String(d['school_name'] ?? ''),
      schoolId: Number(d['school_id']) || 0,
      schoolKeyId: String(d['school_key_id'] ?? ''),
      schoolTypeId: Number(d['school_type_id']) || 0,
      schlId: String(d['schlId'] ?? ''),
      highClass: Number(d['high_class']) || 0,
      lowClass: Number(d['low_class']) || 0,
      udise_code: String(d['udise_code'] ?? ''),

      // teacher
      teacherId: Number(d['teacher_id']) || 0,
      teacherType: String(d['teacher_type'] ?? ''),
      type_teacher: String(d['type_teacher'] ?? ''),
      subjects: String(d['subjects'] ?? ''),
      subject_id: String(d['subject_id'] ?? ''),

      // user type / role
      usertype: String(d['user_type'] ?? ''),
      usertypeid,
      emisUsertype1: String(d['emis_usertype1'] ?? ''),
      roleId: Number(d['roleId']) || 0,
      offKeyId: isOfficeUser ? String(d['off_key_id'] ?? '') : '',

      // category
      cate_id: String(d['cate_id'] ?? ''),
      catty_id: String(d['catty_id'] ?? ''),
      cateType: String(d['cate_type'] ?? ''),
      category_type: String(d['category_type'] ?? ''),
      manage_id: String(d['manage_id'] ?? ''),

      // location
      districtId: String(d['district_id'] ?? ''),
      districtName: String(d['district_name'] ?? ''),
      blockId: String(d['block_id'] ?? ''),
      blockName: String(d['block_name'] ?? ''),
      edu_dist_name: String(d['edu_dist_name'] ?? ''),
      edn_dist_name: String(d['edn_dist_name'] ?? ''),
      // Angular: edn_dist_id takes priority over edu_district_id
      edu_district_id: String(d['edn_dist_id'] ?? d['edu_district_id'] ?? ''),

      // module permissions
      mod2: String(d['mod2'] ?? ''),

      // switch login
      switch_login: String(d['swith_login'] ?? ''), // note: typo in JWT key
      switch_designation: String(d['switch_designation'] ?? ''),

      // token / meta — authToken is the decoded JWT (same as Angular)
      authToken: jwt,
      iat: Number(d['iat']) || 0,
      tokenExpiry: Number(d['exp']) || 0,
    };
  } catch (err) {
    console.error('[Session] Failed to decode token:', err);
    return null;
  }
}
